<template>
    <div class="menu-page mt-4">
        <page-hero
            :title="greeting"
            subtitle="Sua performance digital em tempo real. Monitore o crescimento da sua presença na saúde com inteligência e precisão."
            eyebrow="Visão geral"
        >
            <template #actions>
                <v-btn
                    outlined
                    color="primary"
                    class="elevation-0"
                >
                    <v-icon
                        left
                        size="18"
                    >
                        mdi-calendar-month-outline
                    </v-icon>
                    Últimos 30 dias
                </v-btn>
            </template>
        </page-hero>

        <!-- Analytics (dados reais do Duda) -->
        <section class="dashboard-analytics mb-12">
            <analytics-section
                :analytics="analytics"
                :loading="analyticsLoading"
            />
        </section>

        <!-- Hero cards -->
        <section class="mb-12">
            <section-header
                title="Acesso rápido"
                icon="mdi-auto-fix"
            >
                <template #actions>
                    <eyebrow primary>Personalize seu fluxo</eyebrow>
                </template>
            </section-header>

            <div class="dashboard-hero-grid">
                <hero-card
                    v-for="hero in visibleHeroes"
                    :key="hero.title"
                    :title="hero.title"
                    :description="hero.description"
                    :icon="hero.icon"
                    :to="hero.to"
                    :cta="hero.cta"
                />
            </div>
        </section>

        <!-- Activity + Support -->
        <section>
            <section-header
                title="Últimas publicações"
                icon="mdi-pulse"
            />
            <div class="dashboard-bottom-grid">
                <activity-list
                    :items="activities"
                    title="Registro de ações recentes"
                    :action-label="''"
                />
                <support-panel
                    @action="goToSupport"
                />
            </div>
        </section>
    </div>
</template>

<script>
export default {
    name: 'DashboardView',

    data() {
        return {
            analytics: null,
            analyticsLoading: true,
            heroes: [
                {
                    title: 'Presença GEO e SEO',
                    description: 'Acompanhe seu nome nas respostas das IAs e nos resultados do Google.',
                    icon: 'mdi-text-box-search-outline', to: '/presenca-geo-seo', cta: 'Ver presença', permission: 'GEO_SEO_PRESENCE',
                },
                {
                    title: 'Artigos com IA',
                    description: 'Gere artigos otimizados para SEO em minutos e publique direto no seu site.',
                    icon: 'mdi-feather',
                    to: '/articles',
                    cta: 'Criar artigo',
                    permission: 'ARTICLE_ASSISTANT',
                },
                {
                    title: 'Secretária IA',
                    description: 'Sua assistente inteligente para agendamentos e suporte ao paciente 24/7.',
                    icon: 'mdi-account-tie-woman',
                    to: '/assistants',
                    cta: 'Configurar',
                    permission: 'SECRETARY_ASSISTANT',
                },
                {
                    title: 'Redes sociais',
                    description: 'Crie posts consistentes com a sua marca em todas as redes ao mesmo tempo.',
                    icon: 'mdi-share-variant-outline',
                    to: '/social-assistant',
                    cta: 'Gerar posts',
                    permission: 'SOCIAL_NETWORK_ASSISTANT',
                },
                {
                    title: 'Perfil no Google',
                    description: 'Acompanhe como pacientes encontram e interagem com seu consultório no Google.',
                    icon: 'mdi-google-maps',
                    to: '/perfil-no-google',
                    cta: 'Ver desempenho',
                    permission: 'GOOGLE_BUSINESS_PROFILE',
                },
            ],
            activities: [],
        }
    },

    computed: {
        currentUser() {
            return this.$store.state.currentUser
        },
        isAdmin() {
            return this.currentUser?.admin
        },
        visibleHeroes() {
            const perms = this.currentUser?.permissions || []
            return this.heroes.filter(hero => this.isAdmin || perms.includes(hero.permission))
        },
        greeting() {
            const hour = new Date().getHours()
            const period = hour < 5
                ? 'Boa madrugada'
                : hour < 12
                    ? 'Bom dia'
                    : hour < 18
                        ? 'Boa tarde'
                        : 'Boa noite'

            const name = this.currentUser?.name
            if (!name) return `${period}!`

            const firstName = name.split(' ')[0]
            return `${period}, ${firstName}`
        },
    },

    mounted() {
        this.loadAnalytics()
        this.loadActivities()
    },

    methods: {
        async loadActivities() {
            const perms = this.currentUser?.permissions || []
            const canArticles = this.isAdmin || perms.includes('ARTICLE_ASSISTANT')
            const canSocial = this.isAdmin || perms.includes('SOCIAL_NETWORK_ASSISTANT')
            try {
                const userId = this.currentUser?._id
                const [artResp, socResp] = await Promise.all([
                    canArticles ? this.$api.listArticlesV2({ onlyMine: true }) : Promise.resolve(null),
                    canSocial ? this.$api.getSocialPosts({ page: 1, limit: 10, filters: { status: 'published', user_id: userId } }) : Promise.resolve(null),
                ])
                const items = []

                for (const a of (Array.isArray(artResp?.message) ? artResp.message : [])) {
                    if (a.publish_status !== 'published' && a.publish_status !== 'scheduled') continue
                    const scheduled = a.publish_status === 'scheduled'
                    items.push({
                        date: new Date(a.publish_date || a.created),
                        title: scheduled ? 'Artigo agendado' : 'Artigo publicado',
                        description: a.title || a.subject || 'Artigo',
                        icon: scheduled ? 'mdi-calendar-clock' : 'mdi-file-document-check-outline',
                        iconColor: scheduled ? 'secondary' : 'primary',
                    })
                }

                for (const p of (Array.isArray(socResp?.message) ? socResp.message : [])) {
                    items.push({
                        date: new Date(p.changed || p.created),
                        title: 'Post publicado nas redes sociais',
                        description: p.title || (p.text ? p.text.slice(0, 80) : 'Publicação nas redes'),
                        icon: 'mdi-share-variant-outline',
                        iconColor: 'tertiary',
                    })
                }

                items.sort((x, y) => y.date - x.date)
                this.activities = items.slice(0, 4).map(it => ({
                    title: it.title,
                    description: it.description,
                    time: this.$date.relative(it.date),
                    icon: it.icon,
                    iconColor: it.iconColor,
                }))
            } catch (e) {
                this.activities = []
            }
        },
        async loadAnalytics() {
            this.analyticsLoading = true
            try {
                const resp = await this.$api.getSiteAnalytics('30d')
                if (resp && !resp.error) {
                    this.analytics = resp
                } else {
                    this.analytics = { has_site: false }
                }
            } catch (e) {
                this.analytics = { has_site: false }
            } finally {
                this.analyticsLoading = false
            }
        },
        goToSupport() {
            const phone = '5511920917802'
            const message = 'Olá, preciso de suporte com o sistema da Future'
            window.open(`https://wa.me/${phone}?text=${encodeURIComponent(message)}`, '_blank')
        },
    },
}
</script>

<style scoped>
.dashboard-hero-grid {
    display: grid;
    grid-template-columns: repeat(1, 1fr);
    gap: 32px;
}

.dashboard-bottom-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 24px;
}

@media (min-width: 768px) {
    .dashboard-hero-grid {
        grid-template-columns: repeat(2, 1fr);
    }
}

@media (min-width: 1200px) {
    .dashboard-hero-grid {
        grid-template-columns: repeat(3, 1fr);
    }
    .dashboard-bottom-grid {
        grid-template-columns: 2fr 1fr;
    }
}
</style>
